import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { FormFieldInterface } from 'components/Form/Form.types';
import Form from 'components/Form/Form';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { CreateQuestionInterface, CreateStepInterface } from 'pages/Activities/activities.context';
import { useActivityWizardContext } from 'pages/Activities/ActivityWizard/activityWizard.context';
import { useDashboardContext } from 'context/dashboard.context';
import SingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/SingleSelectDropdown';
import UserSingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/Usage/UsersSingleSelectDropdown';
import ReorderableComponent from 'components/ReorderableComponent/ReorderableComponent';
import CustomFileUpload from './CustomFileUpload';
import { QuestionType, UpdateQuestionChoiceInput } from '__generated__/graphql';
import QuestionChoicesEdit from 'pages/Activities/ActivityWizard/StepDetails/QuestionChoicesEdit';

interface StepProps {
  stepIndex: number;
  question?: boolean;
}

const Step = ({ stepIndex, question }: StepProps): JSX.Element => {
  const { md } = useDashboardContext();
  const { newActivity, setNewActivity, existingActivity } = useActivityWizardContext();
  const newStep = newActivity.steps[stepIndex];
  const { remove } = newStep;
  const timeUnitOptions: DropdownOption[] = [
    { id: 1, name: 'Minute(s)' },
    { id: 2, name: 'Hour(s)' },
  ];
  const [estimatedTimeToCompleteUnit, setEstimatedTimeToCompleteUnit] = useState<
    DropdownOption | undefined
  >(timeUnitOptions[0]);

  const questionTypeOptions: DropdownOption[] = [
    { id: 0, name: 'Text' },
    { id: 1, name: 'Choice' },
  ];

  const getSelected = (name?: string) => questionTypeOptions.find((option) => option.name === name);

  const editStep = (
    value: string | number | number[] | boolean | undefined | File[],
    key: keyof CreateStepInterface
  ) => {
    const steps = [...newActivity.steps];
    if (key == 'estimatedTimeToComplete' && estimatedTimeToCompleteUnit?.name === 'Hour(s)') {
      steps[stepIndex] = { ...newStep, [key]: Number(value) * 60 };
    } else {
      steps[stepIndex] = { ...newStep, [key]: value };
    }

    setNewActivity({ ...newActivity, steps });
  };

  const editQuestion = (
    value: string | number | number[] | boolean | undefined | UpdateQuestionChoiceInput[],
    key: keyof CreateQuestionInterface,
    existingStepState?: CreateStepInterface
  ) => {
    const steps = [...newActivity.steps];
    const stepState = existingStepState || newStep;
    steps[stepIndex] = { ...stepState, question: { ...stepState.question!, [key]: value } };
    setNewActivity({ ...newActivity, steps });

    return steps[stepIndex];
  };

  const removeStep = () => {
    const steps = [...newActivity.steps];

    if (existingActivity?.steps[stepIndex].id) {
      steps[stepIndex].remove = !steps[stepIndex].remove;
    } else {
      steps.splice(stepIndex, 1);
    }

    setNewActivity({ ...newActivity, steps });
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'Subject',
      value: newStep.subject,
      onChange: (subject) => editStep(subject, 'subject'),
      fullWidth: true,
      // requiredMinimumLength: 1, // TODO - Removed until Activity Wizard form validation ticket
      disabled: remove,
    },
    {
      label: 'Description',
      value: newStep.description,
      onChange: (description) => editStep(description, 'description'),
      fullWidth: true,
      hide: !!question,
      disabled: remove,
      multiLine: true,
      richTextEditor: true,
    },
    {
      label: 'Upload',
      type: 'file',
      onChange: () => {},
      customField: (
        <CustomFileUpload
          step={newStep}
          stepIndex={stepIndex}
          onChange={(files) => editStep(files, 'files')}
        />
      ),
    },
    {
      label: 'Start Date',
      type: 'date',
      value: newStep.startDate,
      onChange: (startDate) => editStep(startDate, 'startDate'),
      md: 6,
      disabled: remove,
    },
    {
      label: 'Due Date',
      type: 'date',
      value: newStep.dueDate,
      onChange: (dueDate) => editStep(dueDate, 'dueDate'),
      md: 6,
      disabled: remove,
    },
    {
      label: 'Estimated Time to Complete',
      type: 'number',
      value:
        estimatedTimeToCompleteUnit?.name === 'Hour(s)'
          ? (Math.round((newStep.estimatedTimeToComplete / 60) * 100) / 100).toString()
          : (Math.round(newStep.estimatedTimeToComplete * 100) / 100).toString(),
      onChange: (estimatedTimeToComplete) =>
        editStep(Number(estimatedTimeToComplete), 'estimatedTimeToComplete'),
      md: 8,
      hide: !!question,
      disabled: remove,
    },
    {
      type: 'dropdown',
      onChange: () => editStep(newStep.estimatedTimeToComplete, 'estimatedTimeToComplete'),
      customField: (
        <SingleSelectDropdown
          fullWidth
          marginTop='-1rem'
          variant='outlined'
          allOptions={timeUnitOptions}
          selected={estimatedTimeToCompleteUnit}
          setSelected={(selectedOption?: DropdownOption | undefined) =>
            setEstimatedTimeToCompleteUnit(selectedOption)
          }
          disableClearable={true}
          sx={md ? { marginTop: '1.6rem' } : {}}
        />
      ),
      md: 4,
      hide: !!question,
      disabled: remove,
    },
    {
      label: 'Assigned To',
      onChange: () => {},
      customField: (
        <UserSingleSelectDropdown
          label='Assigned To'
          disabled={remove}
          selectedUserId={newActivity.steps[stepIndex]?.assignedUserId}
          onChange={(selectedOption?: DropdownOption) =>
            editStep(selectedOption?.id, 'assignedUserId')
          }
        />
      ),
      fullWidth: true,
      disabled: remove,
    },
    {
      label: 'Requires Review',
      type: 'checkbox',
      checked: newStep.requiresReview,
      onChange: () => {},
      hide: !!question,
      onCheck: () => editStep(!newStep.requiresReview, 'requiresReview'),
      disabled: remove,
    },
    {
      label: 'Question Details',
      onChange: (questionDetail) => editQuestion(questionDetail, 'questionDetail'),
      value: newStep.question?.questionDetail,
      hide: !question,
      disabled: remove,
    },
    {
      label: 'Question Type',
      onChange: () => {},
      hide: !question,
      disabled: remove,
      customField: (
        <SingleSelectDropdown
          disabled={remove}
          fieldLabel='Question Type'
          allOptions={questionTypeOptions}
          fullWidth={true}
          selected={getSelected(newStep.question?.type)}
          setSelected={(selectedOption?: DropdownOption) => {
            const updatedStep = editQuestion(selectedOption?.name, 'type');
            if (selectedOption?.name !== QuestionType.Choice) {
              editQuestion([], 'questionChoices', updatedStep);
            }
          }}
        />
      ),
    },
    {
      label: 'Multiple Choice',
      hide: !question || newStep.question?.type !== QuestionType.Choice,
      type: 'checkbox',
      checked: newStep.question?.isMultiple,
      onChange: () => {},
      onCheck: () => editQuestion(!newStep.question?.isMultiple, 'isMultiple'),
      disabled: remove,
    },
    {
      onChange: () => {},
      hide: !question || newStep.question?.type !== QuestionType.Choice,
      disabled: remove,
      customField: (
        <QuestionChoicesEdit
          disabled={remove}
          onChange={(newValues) => {
            editQuestion(newValues, 'questionChoices');
          }}
          value={newStep.question?.questionChoices}
        />
      ),
    },
    {
      label: 'Required for Activity Completion',
      type: 'checkbox',
      checked: newStep.isRequired,
      onChange: () => {},
      onCheck: () => editStep(!newStep.isRequired, 'isRequired'),
      disabled: remove,
    },
  ];

  return (
    <Grid container sx={{ marginBottom: '2rem' }}>
      <Grid item xs={0} md={2} />
      <Grid item xs={12} md={8}>
        {/* We add these empty functions here so that the icons will show as it has been requested by QA */}
        <ReorderableComponent
          title={`Step ${stepIndex + 1}`}
          onDelete={removeStep}
          removed={remove}
        >
          <Form fields={fields} />
        </ReorderableComponent>
      </Grid>
    </Grid>
  );
};

export default Step;
